import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";

import { useSignInMutation } from "@sso/apis/login";
import { useResendActivationEmailMutation } from "@sso/apis/registration";
import { AuthHeader } from "@sso/components/AuthHeader";
import { FormikPasswordInput } from "@sso/components/Formik";
import { FormikTextInput } from "@sso/components/Formik/FormikTextInput";
import { SubmitLoadingButton } from "@sso/components/SubmitLoadingButton";
import { ActivationEmailKeys, ApiResultCode, AuthKeys, PathKeys, signInInitialValues } from "@sso/constant";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { selectActivationEmailStatus, setActivationEmailStatus } from "@sso/store/slices/auth";
import { ApiErrors, SignInType } from "@sso/types";
import { CustomLink } from "@sso/ui/CustomLink";
import { RowContainer } from "@sso/ui/FlexBox";

import { signInValidation } from "./schema";

export const SignIn = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const resendEmailActivation = useAppSelector(selectActivationEmailStatus);

  const [signInUser, { isLoading: signinLoading }] = useSignInMutation();
  const [resendActivationEmail, { isLoading: resendEmailLoading }] = useResendActivationEmailMutation();

  const isLoading = signinLoading || resendEmailLoading;
  const isShowingResendEmail = resendEmailActivation !== ActivationEmailKeys.DISABLE;

  const handleSignIn = async (values: SignInType) => {
    try {
      await signInUser(values).unwrap();
      navigate(PathKeys.ROOT);
    } catch (error) {
      const { data: apiErrors } = error as { data: ApiErrors };
      if (apiErrors.resultCode === ApiResultCode.EmailNotConfirmed) {
        dispatch(setActivationEmailStatus(ActivationEmailKeys.ENABLE));
      }
    }
  };

  const handleResendActivationEmail = (values: SignInType) => {
    resendActivationEmail(values);
  };

  return (
    <>
      <AuthHeader title="Hi, Welcome Back!" />
      <Formik
        initialValues={signInInitialValues}
        validationSchema={signInValidation}
        onSubmit={isShowingResendEmail ? handleResendActivationEmail : handleSignIn}
      >
        <Form>
          <FormikTextInput name={AuthKeys.EMAIL_USER_NAME} label="User Name / Email" />
          <FormikPasswordInput name={AuthKeys.PASSWORD} label="Password" />
          <SubmitLoadingButton
            loading={isLoading}
            disabledButton={resendEmailActivation === ActivationEmailKeys.SENT_EMAIL}
            buttonTitle={isShowingResendEmail ? "Resend Activation Email" : "Sign In"}
          />
        </Form>
      </Formik>
      <RowContainer justify="space-between" width="100%" padding="0 0.75rem" margin="1.5rem 0 0.5rem 0">
        <CustomLink to={PathKeys.SIGN_UP} fontWeight={500}>
          Don't have an account?
        </CustomLink>
        <CustomLink to={PathKeys.FORGOT_PASSWORD} color={theme.colors.primary.main} fontWeight={500}>
          Forgot Password?
        </CustomLink>
      </RowContainer>
    </>
  );
};
